import { usePermissionsStore } from "@/store/permissions";
import { storeToRefs } from "pinia";

export type PermissionAction = "view" | "create" | "update" | "delete";

export function usePermissions() {
  const permissionsStore = usePermissionsStore();
  const { permissionsMap } = storeToRefs(permissionsStore);

  /**
   * Check if the user has a specific permission for a module.
   * @param moduleKey - The unique key of the module (e.g., 'faq_s_6114')
   * @param action - The action to check ('view', 'create', 'update', 'delete')
   * @returns boolean
   */
  const hasPermission = (moduleKey: string, action: PermissionAction): boolean => {
    // Admin override: always return true
    if (permissionsStore.isAdmin) return true;

    const modulePerms = permissionsMap.value[moduleKey];
    if (!modulePerms) return false;

    return !!modulePerms[action];
  };

  /**
   * Higher order check: Can view the module at all?
   */
  const canView = (moduleKey: string) => hasPermission(moduleKey, "view");
  
  /**
   * Higher order check: Can create in the module?
   */
  const canCreate = (moduleKey: string) => hasPermission(moduleKey, "create");
  
  /**
   * Higher order check: Can edit/update in the module?
   */
  const canUpdate = (moduleKey: string) => hasPermission(moduleKey, "update");
  
  /**
   * Higher order check: Can delete from the module?
   */
  const canDelete = (moduleKey: string) => hasPermission(moduleKey, "delete");

  return {
    hasPermission,
    canView,
    canCreate,
    canUpdate,
    canDelete,
  };
}
